import { Link } from "react-router-dom";
import { Phone, QrCode, Menu, ShieldCheck, User, LogOut } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { TIDS } from "@/constants/testIds";
import { getUser, clearAuth } from "@/lib/api";

const COMPANY_PHONE = "+64225250123";

export default function Header() {
  const [open, setOpen] = useState(false);
  const user = getUser();
  const isRider = user?.role === "rider";
  const bookUrl = typeof window !== "undefined" ? `${window.location.origin}/book/qr` : "/book/qr";
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&bgcolor=111827&color=E5A93C&data=${encodeURIComponent(bookUrl)}`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-white/10 bg-[#090D14]/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid={TIDS.nav.home} className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-[#090D14] font-black text-lg font-display">A</div>
          <div className="leading-tight">
            <div className="font-display font-extrabold tracking-tight text-white text-lg">Triptap Taxi service</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 font-mono -mt-0.5">Executive · NZ</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm text-slate-300 hover:text-white transition-colors">Home</Link>
          <a href="/#fleet" className="text-sm text-slate-300 hover:text-white transition-colors">Fleet</a>
          <a href="/#contact" className="text-sm text-slate-300 hover:text-white transition-colors">Contact</a>
          {isRider && <Link to="/my-rides" data-testid="nav-my-rides" className="text-sm text-slate-300 hover:text-amber-400 transition-colors">My rides</Link>}
          <Link to="/admin/login" data-testid={TIDS.nav.adminLogin} className="text-sm text-slate-400 hover:text-amber-400 transition-colors flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Admin
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={TIDS.nav.qrBtn} className="text-slate-300 hover:text-amber-400 hover:bg-white/5">
                <QrCode className="w-5 h-5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#111827] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-display">Scan to Book</DialogTitle>
                <DialogDescription className="text-slate-400">Point your camera at the code — you'll land straight on our booking form.</DialogDescription>
              </DialogHeader>
              <div className="grid place-items-center py-4">
                <img src={qrSrc} alt="Booking QR code" className="rounded-xl border border-amber-400/30" />
              </div>
              <p className="text-center text-xs font-mono text-amber-400/80 uppercase tracking-widest">apex-taxi · quick-book</p>
            </DialogContent>
          </Dialog>

          <a href={`tel:${COMPANY_PHONE}`} data-testid={TIDS.nav.callBtn} className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-200 hover:text-amber-400 px-3 py-2 rounded-lg border border-white/10 hover:border-amber-400/40 transition-colors">
            <Phone className="w-4 h-4" />
            <span className="font-mono">{COMPANY_PHONE}</span>
          </a>

          <Link to="/book" data-testid={TIDS.nav.book}>
            <Button className="btn-gold rounded-full px-5">Book Ride</Button>
          </Link>

          {isRider ? (
            <div className="hidden sm:flex items-center gap-1 ml-1">
              <span className="text-xs text-slate-400 px-2 font-mono">{user.name}</span>
              <Button variant="ghost" size="icon" onClick={() => { clearAuth(); window.location.reload(); }} data-testid="nav-signout" className="text-slate-300 hover:text-red-400 hover:bg-white/5"><LogOut className="w-4 h-4" /></Button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-signin" className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 px-3 py-2 rounded-lg border border-white/10 hover:border-amber-400/40 transition-colors">
              <User className="w-4 h-4" /> Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
